import React, { useEffect, useRef, useState } from 'react'
import { Box, Card, Checkbox, colors, Flex, FormField, PrimaryButton, SecondaryButton, space, TertiaryButton, Text, Tooltip } from '@workday/canvas-kit-react'
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api'
import IncidentCard from './IncidentCard'
import TimeDisplayOption from '../models/TimeDisplayOptions'
import { PrimaryButtonColors } from '../../components/CanvasThemes'
import { AgencyModel } from '../../models/AgencyModel'
import { IncidentFeedModel } from '../../models/IncidentFeedModel'
import { IncidentModel } from '../../models/IncidentModel'
import styled from '@emotion/styled'
import { caretDownIcon, caretUpIcon, loopIcon } from '@workday/canvas-system-icons-web'
import { AEDModel, Coordinate, Environment } from '../../models'
import { fetchAEDs, fetchMapLayers } from '../models/WebAppRequests'
import { CognitoUserSession } from 'amazon-cognito-identity-js'
import { MapLayerModel } from '../../models/MapLayerModel'
import { Centered } from '../../components'
import MapLayerModal from './MapLayerModal'

export type IncidentMapViewport = {
  center: Coordinate
  zoomLevel?: number
}

export type IncidentMapProps = {
  isSmallScreenWidth: boolean
  userLocation?: {lat:number, lng: number}
  timeDisplayOption: TimeDisplayOption
  agencies: AgencyModel[]
  feedData: IncidentFeedModel
  viewport?: IncidentMapViewport
  onSelectIncident: (incident: IncidentModel) => void
  onMapMove: (viewport?: IncidentMapViewport) => void
  env: Environment
  session?: CognitoUserSession
}

const StyledMapLayerButton = styled(TertiaryButton)`
  font-family: Roboto, Arial, sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: ${colors.blackPepper400};
  width: 105px;

  &:hover,
  &:active,
  &:focus {
    font-weight: 500;
    color: ${colors.blackPepper500} !important;
  }
  
  span {
    text-decoration: none !important;

    .wd-icon-fill {
      fill: ${colors.blackPepper500} !important;
    }
  }
`

const MapLayerConatiner = styled.div<{isSmallScreen: boolean}>`
  ${(props) => props.isSmallScreen ? 'bottom: 30px;' : 'top: 10px;' }
  ${(props) => props.isSmallScreen ? 'left: 10px;' : 'right: 60px;' }
  position: absolute;
  z-index: 10;
  opacity: 0.95;
`

const MapLayerButtonConatiner = styled.div`
  background: white;
  border: 0px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
`

const DisplayResponderCard = styled(Card)<{isSmallScreen: boolean}>`
  position: absolute;
  z-index: 10;
  opacity: 0.95;
  width: 155px;
  padding-top: 8px;
  padding-bottom: 8px;
  border-radius: 0px;
  border: none;
  ${(props) => props.isSmallScreen ? 'bottom: 41px;' : 'top: 41px;' }

  > div {
   margin: 0px;
  }
  
  label {
    font-size: 13px;
  }
`

const AEDImage = styled.img`
  max-width: 100px;
  max-height: 100px;
`

const StyledTertiaryButton = styled(SecondaryButton)<{isSmallScreen: boolean}>`
  position: absolute;
  top: 10px;
  ${(props) => props.isSmallScreen ? 'left: calc(50vw - 58px);' : 'left: calc(50vw - 283px);' }
  padding: 10px 20px;
  z-index: 11;
  background: white;
  border: 0px;
  box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
`

const minMapZoomLevelForAEDs = 11
const availableMapLayers = [{ 
  id: 'Stations', 
  name: 'Stations',
  minZoom: 12
},{ 
  id: 'Cameras', 
  name: 'Traffic Cameras', 
  minZoom: 12
}]

const IncidentMap = ({ isSmallScreenWidth, timeDisplayOption, agencies, feedData, viewport, onSelectIncident, onMapMove, session, env, userLocation }: IncidentMapProps) => {
  const [map, setMap] = useState<any | null>(null)
  const [selectedMarkerID, setSelectedMarkerID] = useState<string | undefined>(undefined)
  const [mapLayers, setMapLayers] = useState<Record<string, MapLayerModel[]>>()
  const [showMapLayers, setShowMapLayers] = useState<string[]>([])
  const [selectedMapLayer, setSelectedMapLayer] = useState<MapLayerModel>()
  const [aeds, setAEDs] = useState<AEDModel[]>()
  const [showAEDs, setShowAEDs] = useState<boolean>(false)
  const [curZoomLevel, setCurZoomLevel] = useState<number>(-1)
  const [curBounds, setCurBounds] = useState<any | null>(null)
  const [showResetButton, setShowResetButton] = useState<boolean>(viewport !== undefined)
  const isInitialLoadRef = useRef(true)

  const [isLayerMenuOpen, setIsLayerMenuOpen] = useState(false)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const buttonRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    setIsLayerMenuOpen(true);
  };

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsLayerMenuOpen(false);
    }, 200);
  };

  const handleToggle = () => {
    setIsLayerMenuOpen(!isLayerMenuOpen);
  };

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (map === null) return

    if (!viewport) {
      const bounds = new window.google.maps.LatLngBounds()
      const incidents = feedData.mappableIncidents()
      if (incidents.length > 0) {
        incidents.forEach((incident: IncidentModel) => bounds.extend(incident.locationCoordinate))
      } else {
        agencies.forEach(agency => agency.boundaryCornerCoordinates().forEach(coordinate => bounds.extend(coordinate)))
      }
      isInitialLoadRef.current = true

      // Don't zoom in too far
      const minSpan = 0.01
      const latSpan = 
        Math.abs(bounds.getNorthEast().lat() - bounds.getSouthWest().lat())
      const lngSpan = 
        Math.abs(bounds.getNorthEast().lng() - bounds.getSouthWest().lng())

      if (latSpan < minSpan && lngSpan < minSpan) {
        map.setCenter(bounds.getCenter())
        map.setZoom(15)
      } else {
        map.fitBounds(bounds)
      }
    }
  }, [map, feedData, agencies])

  useEffect(() => {
    if (!userLocation || map === null) return
    
    map.panTo(userLocation)
    map.setZoom(map.getZoom() <= 14 ? 14 : map.getZoom())
  }, [userLocation, map])

  useEffect(() => {
    if (curBounds === null) return

    const northEast = curBounds.getNorthEast()
    const southWest = curBounds.getSouthWest()
    const curViewport = `${southWest.lat()},${northEast.lat()},${southWest.lng()},${northEast.lng()}`
    
    if (map.getZoom() >= minMapZoomLevelForAEDs) {
        fetchAEDs(
        curViewport, 
        env, 
        session
      ).then((aeds) => {
          setAEDs(aeds)
      })
    }

    const layers = availableMapLayers.map((layer) => 
      map.getZoom() >= layer.minZoom && layer.id
    ).filter(Boolean).join(',')

    if (layers.length > 0) {
      fetchMapLayers (
        curViewport, 
        layers,
        env, 
        session
      ).then((layers) => {
          setMapLayers(layers)
      })
    }
  }, [curBounds])

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_API_KEY as string
  })

  const agencyForAgencyID = (agencyID: string) => {
    return agencies.find(agency => agency.agencyID === agencyID)!
  }

  return (
    <Box flex="1" position='relative'>
      {showResetButton && (
        <StyledTertiaryButton 
          isSmallScreen={isSmallScreenWidth}
          onClick={() => {
            isInitialLoadRef.current = true
            onMapMove()
            setShowResetButton(false)
          }} 
          icon={loopIcon} 
          iconPosition="start"
        >
          Reset Map
        </StyledTertiaryButton>
      )}
      <MapLayerConatiner isSmallScreen={isSmallScreenWidth}>
        <MapLayerButtonConatiner
          ref={buttonRef}
          onMouseLeave={handleMouseLeave}
        >
          <StyledMapLayerButton
            icon={isLayerMenuOpen ? caretUpIcon : caretDownIcon} 
            onClick={handleToggle}
          >
            Layers
          </StyledMapLayerButton>
        </MapLayerButtonConatiner>
        {isLayerMenuOpen && (
          <div
            ref={containerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <DisplayResponderCard padding={space.xs} isSmallScreen={isSmallScreenWidth}>
              {curZoomLevel < minMapZoomLevelForAEDs ? (
                <Tooltip title="Zoom In to view AEDs">
                  <FormField>
                    <Checkbox
                      disabled={true}
                      checked={showAEDs}
                      label='AEDs'
                    />
                  </FormField>
                </Tooltip>
              ) : (
                <FormField>
                  <Checkbox
                    checked={showAEDs}
                    label='AEDs'
                    onChange={() => setShowAEDs(!showAEDs)}
                  />
                </FormField>
              )}
              {availableMapLayers.map((layer) => {
                return curZoomLevel < layer.minZoom ? (
                  <Tooltip title={`Zoom In to view ${layer.name}`} key={layer.id}>
                    <FormField>
                      <Checkbox
                        disabled={true}
                        checked={showMapLayers.includes(layer.id)}
                        label={layer.name}
                      />
                    </FormField>
                  </Tooltip>
                ) : (
                  <FormField key={layer.id}>
                    <Checkbox
                      checked={showMapLayers.includes(layer.id)}
                      label={layer.name}
                      onChange={() => {
                        if (showMapLayers.includes(layer.id)) {
                          setShowMapLayers(showMapLayers.filter((id) => layer.id !== id))
                        } else {
                          setShowMapLayers([...showMapLayers, layer.id])}
                        }
                      }
                    />
                  </FormField>
                )
              })}
            </DisplayResponderCard>
          </div>
        )}
      </MapLayerConatiner>
      {isMapLoaded && (
        <GoogleMap
          onIdle={() => {
            setCurZoomLevel(map?.getZoom())
            setCurBounds(map?.getBounds())
            if (!isInitialLoadRef.current) {
              onMapMove({
                center: {
                  lat: map?.getCenter()?.lat(),
                  lng: map?.getCenter()?.lng()
                }, 
                zoomLevel: map?.getZoom()
              })
              setShowResetButton(true)
            } else {
              isInitialLoadRef.current = false
            }
          }}
          onLoad={map => {
            setMap(map)
          }}
          mapContainerStyle={{
            height: '100%',
            width: '100%'
          }}
          zoom={viewport?.zoomLevel ?? minMapZoomLevelForAEDs}
          center={viewport?.center}
          options={isSmallScreenWidth ? {
            rotateControl: false,
            panControl: false,
            streetViewControl: false,
            scaleControl: false,
            mapTypeControlOptions: {
              style: 2
            }
          } : {
            rotateControl: false,
            panControl: false,
            streetViewControl: false,
            scaleControl: false
          }}
        >
          {aeds && showAEDs && aeds.map((aed: AEDModel) => {
            return !aed.hidden && (
              <Marker
                options={{icon: aed.restricted ? '/images/private_aed_map.png': '/images/public_aed_map.png', title: 'AED Location'}}
                position={{lat: aed.latitude, lng: aed.longitude}}
                onClick={event => setSelectedMarkerID(aed.id)}
                key={`marker-${aed.id}`}
              >
                {selectedMarkerID === aed.id && (
                  <InfoWindow onCloseClick={() => setSelectedMarkerID(undefined)}>
                    <Flex flexDirection="row" gap={space.xs}>
                      {aed.imageURL && (
                        <AEDImage src={`https://api.pulsepoint.org/v1${aed.imageURL}?x=100`} />
                      )}
                      <Flex flexDirection="column" gap={space.xs} minWidth={100} maxWidth={300}>
                        <Text typeLevel="body.medium" fontWeight="bold">
                          {aed.name}
                        </Text>
                        <Text typeLevel="body.small">
                          {aed.description}
                        </Text>
                      </Flex>
                    </Flex>
                  </InfoWindow>
                )}
              </Marker>
            )
          })}
          {mapLayers && showMapLayers.flatMap((mapLayer) => 
            mapLayers[mapLayer] && mapLayers[mapLayer].map((layer: MapLayerModel) => 
              (
                <Marker
                  options={{
                    icon: `https://api.pulsepoint.org/v1/icons/${layer.icon}.png?y=40`, 
                    title: layer.itemName
                  }}
                  position={{lat: layer.latitude, lng: layer.longitude}}
                  onClick={event => setSelectedMarkerID(layer.id)}
                  key={`marker-${layer.id}`}
                >
                  {selectedMarkerID === layer.id && !selectedMapLayer && (
                    <InfoWindow onCloseClick={() => setSelectedMarkerID(undefined)}>
                      <Flex flexDirection="column" gap={space.xs} minWidth={100} maxWidth={300}>
                        <Centered>
                          <Text typeLevel="body.medium" fontWeight="bold">
                            {layer.itemName}
                          </Text>
                        </Centered>
                        <Centered>
                          <TertiaryButton onClick={() => setSelectedMapLayer(layer)}>
                            View More
                          </TertiaryButton>
                        </Centered>
                      </Flex>
                    </InfoWindow>
                  )}
                </Marker>
              )
            )
          )}
          {userLocation && (
            <Marker
              position={userLocation}
              icon={{
                path: window.google.maps.SymbolPath.CIRCLE,
                scale: 7,
                fillColor: "#4285F4",
                fillOpacity: 1,
                strokeColor: "#FFFFFF",
                strokeWeight: 2
              }}
            />
          )}
          {feedData.mappableIncidents().map((incident: IncidentModel) => {
            const agency = agencyForAgencyID(incident.agencyID)
            return (
              <Marker
                options={{
                  icon: {
                    url: incident.mapPinIconURL(),
                    scaledSize: new google.maps.Size(incident.mapPinIconSize(), incident.mapPinIconSize())
                  },
                  title: 'Incident Location'
                }}
                position={incident.locationCoordinate}
                onClick={event => setSelectedMarkerID(incident.recordID)}
                key={`marker-${incident.recordID}`}
              >
                {selectedMarkerID === incident.recordID && (
                  <InfoWindow onCloseClick={() => setSelectedMarkerID(undefined)}>
                    <Flex flexDirection="column" gap={space.xs}>
                      <IncidentCard incident={incident} agency={agency} timeDisplayOption={timeDisplayOption} showAgencyTitle={agencies.length !== 1} />
                      <PrimaryButton onClick={event => onSelectIncident(incident)} colors={PrimaryButtonColors}>
                        View Details
                      </PrimaryButton>
                    </Flex>
                  </InfoWindow>
                )}
              </Marker>
            )
          })}
        </GoogleMap>
      )}
      {selectedMapLayer && (
        <MapLayerModal
          item={selectedMapLayer}
          isSmallScreen={isSmallScreenWidth}
          onHide={() => setSelectedMapLayer(undefined)} />
      )}
    </Box>
  )
}

export default IncidentMap
